import { Injectable } from '@angular/core';
import { addMonths, format } from 'date-fns';
import { isObservable, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { IdDescription, LabelValue } from '../models';
import { FormatService } from './format.service';

@Injectable({
  providedIn: 'root',
})
export class UtilityService {
  constructor(private formatService: FormatService) {}

  isNumber(value: any): value is number {
    return !isNaN(this.toInteger(value));
  }

  toInteger(value: any): number {
    return parseInt(value, 10);
  }

  sumByPropertyName<T>(array: T[], property: string): number {
    return array.reduce((total, value) => total + value[property], 0);
  }

  sum<T>(array: T[], property: (model: T) => number): number {
    return array.reduce((total, value) => total + property(value), 0);
  }

  unique<T>(array: T[]): T[] {
    return [...new Set(array)];
  }

  sort<T>(array: T[], property: (prop: T) => any, property2: (prop: T) => any = undefined, ascending = true): T[] {
    return array.sort((a: T, b: T) => {
      if (property(a) < property(b)) return ascending ? -1 : 1;
      if (property(a) > property(b)) return ascending ? 1 : -1;
      if (property2) {
        if (property2(a) < property2(b)) return ascending ? -1 : 1;
        if (property2(a) > property2(b)) return ascending ? 1 : -1;
      }
      return 0;
    });
  }

  chunk<T>(array: T[], chunkSize: number) {
    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
  }

  array1ToN(n: number): number[] {
    return Array.from({ length: n }, (_, i) => i + 1);
  }

  sumPercentageAverage<T>(data: T[], numeratorProperty: string, denominatorProperty: string): string {
    const numerator = this.sumByPropertyName(data, numeratorProperty);
    const denominator = this.sumByPropertyName(data, denominatorProperty);
    const percentage = denominator === 0 ? 0 : (numerator / denominator) * 100;
    return this.formatService.number(percentage, 2);
  }

  mapSelectOptions(options: Observable<IdDescription[]> | IdDescription[]): Observable<LabelValue[]> {
    return (isObservable(options) ? options : of(options))?.pipe(
      map((selectOptions: IdDescription[]) => {
        const results = [];

        if (selectOptions?.length > 0) {
          results.push(
            ...selectOptions.map((option: IdDescription) => ({
              value: option.id,
              label: option.description,
            })),
          );
        }

        return results;
      }),
    );
  }

  mapToSelectOptions<T>(options: T[]): IdDescription[] {
    return options.map((val) => ({ id: val.toString(), description: val.toString() }));
  }

  getLast12Months(firstMonth: string): string[] {
    let monthNumber = new Date().getMonth();
    switch (firstMonth) {
      case 'Jan':
        monthNumber = 0;
        break;
      case 'Feb':
        monthNumber = 1;
        break;
      case 'Mar':
        monthNumber = 2;
        break;
      case 'Apr':
        monthNumber = 3;
        break;
      case 'May':
        monthNumber = 4;
        break;
      case 'Jun':
        monthNumber = 5;
        break;
      case 'Jul':
        monthNumber = 6;
        break;
      case 'Aug':
        monthNumber = 7;
        break;
      case 'Sep':
        monthNumber = 8;
        break;
      case 'Oct':
        monthNumber = 9;
        break;
      case 'Nov':
        monthNumber = 10;
        break;
      case 'Dec':
        monthNumber = 11;
        break;
    }
    const startYear = monthNumber > new Date().getMonth() ? new Date().getFullYear() - 1 : new Date().getFullYear();
    const currentMonth = new Date(startYear, monthNumber, 1);
    return Array.from(Array(12).keys()).map((monthsToAdd) =>
      format(addMonths(currentMonth, monthsToAdd * -1), 'MMM yyyy'),
    );
  }

  getThisYearMonths(year: number): string[] {
    const currentMonth = new Date(Date.UTC(year, 0, 1));
    return Array.from(Array(12).keys()).map((monthsToAdd) => format(addMonths(currentMonth, monthsToAdd), 'MMM yyyy'));
  }

  getFullMonths(): string[] {
    const currentMonth = new Date(Date.UTC(new Date().getFullYear(), 0, 1));
    return Array.from(Array(12).keys()).map((monthsToAdd) => format(addMonths(currentMonth, monthsToAdd), 'MMMM'));
  }

  getAbbreviatedMonths(): string[] {
    const currentMonth = new Date(Date.UTC(new Date().getFullYear(), 0, 1));
    return Array.from(Array(12).keys()).map((monthsToAdd) => format(addMonths(currentMonth, monthsToAdd), 'MMM'));
  }

  getLastYearMonths(previousYear: number): string[] {
    const currentMonth = new Date(Date.UTC(previousYear, 0, 1));
    return Array.from(Array(12).keys()).map((monthsToAdd) => format(addMonths(currentMonth, monthsToAdd), 'MMM yyyy'));
  }

  getEnumValues(T: any): string[] {
    return Object.values(T);
  }

  getPropertyNameFromLambda<T, TResult>(func: (model: T) => TResult): string {
    return func?.toString().split('.')[1];
  }

  getNestedProperty(obj: any, nestedPropertyName: string): any {
    let value = obj;
    const props = nestedPropertyName.split('.');
    for (const prop of props) {
      value = value?.[prop] ?? value;
    }
    return value;
  }

  arrayExecuteAsyncSequentially<T>(data: T[], action: (model: T) => Observable<any>): Observable<any> {
    return data.length == 0
      ? of(true)
      : action(data[0]).pipe(switchMap(() => this.arrayExecuteAsyncSequentially(data.slice(1), action)));
  }
}
